import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ConnectionStatus from './components/ConnectionStatus';
import CredentialsForm from './components/CredentialsForm';
import OAuthFlow from './components/OAuthFlow';
import WebhookConfiguration from './components/WebhookConfiguration';
import ConfigurationGuide from './components/ConfigurationGuide';
import TokenExpirationInfo from './components/TokenExpirationInfo';
import { useNotification } from '../../components/ui/NotificationProvider';
import mercadoLibreService from '../../services/mercadoLibreService';

const APIConfiguration = () => {
  const navigate = useNavigate();
  const { showSuccess, showError, showConfirmation, showInfo } = useNotification();
  const [activeTab, setActiveTab] = useState('credentials');
  const [isLoading, setIsLoading] = useState(false);
  const [isRenewing, setIsRenewing] = useState(false);
  const [isWebhookLoading, setIsWebhookLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [connectionState, setConnectionState] = useState({
    isConnected: false,
    connectionHealth: 'good',
    lastConnected: null,
    tokenExpiry: null
  });

  const [credentials, setCredentials] = useState({
    clientId: '',
    clientSecret: '',
    market: 'MCO'
  });

  const [webhookConfig, setWebhookConfig] = useState({
    n8nEndpoint: '',
    authToken: '',
    isEnabled: false,
    hasAuthToken: false,
    authTokenPreview: null,
    lastTestedAt: null,
    lastDeliveryAt: null
  });

  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || (typeof window !== 'undefined' ? window.location.origin : '');

  const tabs = [
    { id: 'credentials', label: 'Credenciales', icon: 'Key' },
    { id: 'oauth', label: 'Autorización', icon: 'Shield' },
    { id: 'webhooks', label: 'Webhooks', icon: 'Webhook' },
    { id: 'guide', label: 'Guía', icon: 'BookOpen' }
  ].filter((tab) => tab.id !== 'webhooks');

  useEffect(() => {
    // Load saved configuration on mount
    loadSavedConfiguration();
    fetchWebhookConfiguration();
  }, []);

  const loadSavedConfiguration = () => {
    try {
      // Load from environment variables first (for development/production)
      const envCredentials = {
        clientId: import.meta.env.VITE_MERCADOLIBRE_APP_ID || '',
        clientSecret: import.meta.env.VITE_MERCADOLIBRE_CLIENT_SECRET || '',
        market: import.meta.env.VITE_MERCADOLIBRE_SITE_ID || 'MCO'
      };

      // If env variables exist, use them
      if (envCredentials.clientId && envCredentials.clientSecret) {
        setCredentials(envCredentials);
      } else {
        // Fallback to localStorage
        const savedCredentials = localStorage.getItem('ml_credentials');
        if (savedCredentials) {
          setCredentials(JSON.parse(savedCredentials));
        }
      }

      const savedConnection = localStorage.getItem('ml_connection');

      if (savedConnection) {
        setConnectionState(JSON.parse(savedConnection));
      }
    } catch (error) {
      console.error('Error loading configuration:', error);
    }
  };

  const fetchWebhookConfiguration = async () => {
    if (!apiBaseUrl) return;

    try {
      setIsWebhookLoading(true);
      const response = await fetch(`${apiBaseUrl}/api/mercadolibre/webhook-config`);
      const result = await response.json().catch(() => ({ success: false }));

      if (!response.ok || !result?.success) {
        throw new Error(result?.message || 'No se pudo obtener la configuración de webhooks');
      }

      setWebhookConfig({
        n8nEndpoint: result.data?.n8nEndpoint || '',
        isEnabled: Boolean(result.data?.isEnabled),
        hasAuthToken: Boolean(result.data?.hasAuthToken),
        authTokenPreview: result.data?.authTokenPreview || null,
        lastTestedAt: result.data?.lastTestedAt || null,
        lastDeliveryAt: result.data?.lastDeliveryAt || null,
        authToken: ''
      });
    } catch (error) {
      console.warn('No se pudo sincronizar configuración de webhooks:', error?.message || error);
    } finally {
      setIsWebhookLoading(false);
    }
  };

  const handleCredentialsSave = async (formData) => {
    setIsLoading(true);
    setErrors({});

    try {
      // Validaciones locales
      if (!formData?.clientId?.trim()) {
        setErrors({ clientId: 'Client ID es requerido' });
        setIsLoading(false);
        return;
      }
      if (!formData?.clientSecret?.trim()) {
        setErrors({ clientSecret: 'Client Secret es requerido' });
        setIsLoading(false);
        return;
      }

      // Validación real con backend
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/test-mercadolibre-credentials`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientId: formData?.clientId,
          clientSecret: formData?.clientSecret,
          market: formData?.market
        })
      });
      const result = await response.json();

      if (!result?.success) {
        setErrors({ general: result?.message || 'Credenciales inválidas o conexión fallida' });
        showError(result?.message || 'Credenciales inválidas o conexión fallida');
        setIsLoading(false);
        return;
      }

      // Guardar credenciales si son válidas
      localStorage.setItem('ml_credentials', JSON.stringify(formData));
      setCredentials(formData);

      // Si la validación incluye un token de acceso, guardarlo también
      if (result?.data?.access_token) {
        localStorage.setItem('mercadolibre_access_token', result.data.access_token);
        localStorage.setItem('mercadolibre_last_sync', new Date().toISOString());
        localStorage.setItem('mercadolibre_last_validation', new Date().toISOString());
        localStorage.setItem('mercadolibre_app_id', formData.clientId);
        localStorage.setItem('mercadolibre_client_secret', formData.clientSecret);
      }

      showSuccess('Credenciales validadas y guardadas correctamente', {
        actions: [{
          label: 'Continuar',
          onClick: () => setActiveTab('oauth'),
          variant: 'default'
        }]
      });
      setActiveTab('oauth');

    } catch (error) {
      setErrors({ general: 'Error al validar credenciales' });
      showError('Error al validar credenciales. Inténtalo de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOAuthSuccess = (authData) => {
    // Limpiar cualquier error OAuth previo del intento anterior
    setErrors(prev => ({ ...prev, oauth: null }));

    const connectionData = {
      isConnected: true,
      connectionHealth: 'good',
      lastConnected: new Date()?.toISOString(),
      tokenExpiry: new Date(Date.now() + authData.expires_in * 1000)?.toISOString(),
      accessToken: authData?.access_token,
      refreshToken: authData?.refresh_token,
      userId: authData?.user_id
    };

    // Guardar en formato original para compatibilidad
    localStorage.setItem('ml_connection', JSON.stringify(connectionData));

    // También guardar en las claves que espera mercadoLibreService
    localStorage.setItem('mercadolibre_access_token', authData?.access_token);
    localStorage.setItem('mercadolibre_refresh_token', authData?.refresh_token || '');
    localStorage.setItem('mercadolibre_user_id', authData?.user_id || '');
    localStorage.setItem('mercadolibre_last_validation', new Date().toISOString());

    setConnectionState(connectionData);

    showSuccess('¡Conexión establecida correctamente con MercadoLibre!', {
      title: 'Conexión Exitosa',
      actions: [{
        label: 'Ir a Carga Masiva',
        onClick: () => navigate('/bulk-upload'),
        variant: 'default',
        icon: 'Upload'
      }]
    });
  };

  const handleOAuthError = (error) => {
    showError(`Error en la autenticación: ${error}`, {
      title: 'Error de Autenticación',
      persistent: true,
      actions: [{
        label: 'Reintentar',
        onClick: () => window.location?.reload(),
        variant: 'outline'
      }]
    });
  };

  const handleTestConnection = async () => {
    setIsLoading(true);

    try {
      // Simulate API test
      await new Promise(resolve => setTimeout(resolve, 2000));

      if (connectionState?.isConnected) {
        showSuccess('Conexión exitosa - API funcionando correctamente', {
          title: 'Test de Conexión'
        });
      } else {
        showError('No hay conexión establecida', {
          title: 'Test de Conexión Fallido'
        });
      }
    } catch (error) {
      showError(`Error al probar conexión: ${error?.message}`, {
        title: 'Error en Test de Conexión'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisconnect = () => {
    showConfirmation({
      title: 'Confirmar Desconexión',
      message: '¿Estás seguro de que deseas desconectar la API? Deberás volver a autenticarte para usar la aplicación.',
      confirmText: 'Sí, Desconectar',
      cancelText: 'Cancelar',
      type: 'warning',
      onConfirm: () => {
        localStorage.removeItem('ml_connection');
        setConnectionState({
          isConnected: false,
          connectionHealth: 'good',
          lastConnected: null,
          tokenExpiry: null
        });
        showInfo('Desconectado correctamente', {
          title: 'Desconexión Completada'
        });
      }
    });
  };

  const handleWebhookSave = async (webhookData) => {
    if (!apiBaseUrl) {
      showError('No hay una URL base de API configurada', { title: 'Error en Webhooks' });
      return;
    }

    setIsWebhookLoading(true);
    setErrors(prev => ({ ...prev, n8nEndpoint: undefined, authToken: undefined }));

    try {
      const response = await fetch(`${apiBaseUrl}/api/mercadolibre/webhook-config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(webhookData)
      });

      const result = await response.json();

      if (!response.ok || !result?.success) {
        if (response.status === 400) {
          setErrors(prev => ({ ...prev, n8nEndpoint: result?.message }));
        }
        throw new Error(result?.message || 'No se pudo guardar la configuración de webhooks');
      }

      setWebhookConfig({
        n8nEndpoint: result.data?.n8nEndpoint || '',
        isEnabled: Boolean(result.data?.isEnabled),
        hasAuthToken: Boolean(result.data?.hasAuthToken),
        authTokenPreview: result.data?.authTokenPreview || null,
        lastTestedAt: result.data?.lastTestedAt || null,
        lastDeliveryAt: result.data?.lastDeliveryAt || null,
        authToken: ''
      });

      showSuccess('Configuración de webhooks guardada correctamente', {
        title: 'Webhooks Configurados'
      });
    } catch (error) {
      console.error('Error saving webhook config:', error);
      showError(error?.message || 'Error al guardar configuración de webhooks', {
        title: 'Error en Webhooks'
      });
    } finally {
      setIsWebhookLoading(false);
    }
  };

  const handleClearConfiguration = () => {
    showConfirmation({
      title: 'Limpiar Toda la Configuración',
      message: '¿Estás seguro de que deseas eliminar todas las credenciales, conexiones y configuraciones? Esta acción no se puede deshacer.',
      confirmText: 'Sí, Limpiar Todo',
      cancelText: 'Cancelar',
      type: 'danger',
      onConfirm: () => {
        localStorage.removeItem('ml_credentials');
        localStorage.removeItem('ml_connection');
        localStorage.removeItem('ml_webhooks');
        showInfo('Configuración limpiada correctamente. La página se recargará.', {
          title: 'Configuración Limpiada',
          duration: 2000
        });
        setTimeout(() => {
          window.location?.reload();
        }, 2000);
      }
    });
  };

  // Renew connection using refresh token via servicio
  const handleRenewConnection = async () => {
    setIsRenewing(true);
    try {
      const refreshed = await mercadoLibreService.refreshAccessToken();
      if (refreshed) {
        // Update connection state and saved connection
        const newConnection = {
          ...(JSON.parse(localStorage.getItem('ml_connection') || '{}')),
          isConnected: true,
          lastConnected: new Date().toISOString(),
          tokenExpiry: Number(refreshed?.expires_in) > 0
            ? new Date(
                Date.now() +
                Number(refreshed.expires_in) * 1000
              ).toISOString()
            : null
        };
        localStorage.setItem('ml_connection', JSON.stringify(newConnection));
        setConnectionState(newConnection);
        showSuccess('Conexión renovada correctamente.');
      } else {
        // If refresh failed, prompt user to re-authenticate
        showError('No se pudo renovar el token. Por favor vuelve a autenticarte.', { persistent: true });
      }
    } catch (error) {
      console.error('Error renewing connection:', error);
      showError('Error al renovar la conexión. Intenta reconectar manualmente.');
    } finally {
      setIsRenewing(false);
    }
  };

  const isCredentialsConfigured = credentials?.clientId && credentials?.clientSecret;

  return (
    <div className="min-h-screen bg-background">
      <div className="pt-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Settings" size={24} className="text-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-text-primary">Configuración de API</h1>
                <p className="text-text-secondary">Configura tu integración con MercadoLibre</p>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex flex-wrap gap-3">
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                iconName="ArrowLeft"
                iconPosition="left"
              >
                Volver al Dashboard
              </Button>

              {connectionState?.isConnected && (
                <Button
                  variant="default"
                  onClick={() => navigate('/bulk-upload')}
                  iconName="Upload"
                  iconPosition="left"
                >
                  Ir a Carga Masiva
                </Button>
              )}
            </div>
          </div>

          {/* Token Expiration Info */}
          <TokenExpirationInfo />

          {/* Connection Status */}
          <div className="mb-8">
            <ConnectionStatus
              isConnected={connectionState?.isConnected}
              connectionHealth={connectionState?.connectionHealth}
              lastConnected={connectionState?.lastConnected}
              tokenExpiry={connectionState?.tokenExpiry}
              onTestConnection={handleTestConnection}
              onDisconnect={handleDisconnect}
              isLoading={isLoading}
              onRenew={handleRenewConnection}
              isRenewing={isRenewing}
            />
          </div>

          {/* Tab Navigation */}
          <div className="mb-8">
            <div className="border-b border-border">
              <nav className="-mb-px flex space-x-8 overflow-x-auto">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-smooth ${
                      activeTab === tab?.id
                        ? 'border-primary text-primary' :'border-transparent text-text-secondary hover:text-text-primary hover:border-border'
                    }`}
                  >
                    <Icon name={tab?.icon} size={18} />
                    <span>{tab?.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          <div className="space-y-8">
            {activeTab === 'credentials' && (
              <CredentialsForm
                onSubmit={handleCredentialsSave}
                initialData={credentials}
                isLoading={isLoading}
                errors={errors}
              />
            )}

            {activeTab === 'oauth' && (
              <OAuthFlow
                clientId={credentials?.clientId}
                market={credentials?.market}
                onAuthSuccess={handleOAuthSuccess}
                onAuthError={handleOAuthError}
                isEnabled={isCredentialsConfigured}
                onRefreshToken={handleRenewConnection}
                isRefreshing={isRenewing}
              />
            )}

            {activeTab === 'webhooks' && (
              <WebhookConfiguration
                initialData={webhookConfig}
                onSave={handleWebhookSave}
                isLoading={isWebhookLoading}
                errors={errors}
              />
            )}

            {activeTab === 'guide' && (
              <ConfigurationGuide />
            )}
          </div>

          {/* Bottom Actions */}
          <div className="mt-12 pt-8 border-t border-border">
            <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
              <div className="text-sm text-text-secondary">
                <Icon name="Shield" size={16} className="inline mr-2" />
                Tokens y webhooks se almacenan cifrados en el servidor seguro
              </div>

              <div className="flex space-x-3">
                <Button
                  variant="outline"
                  onClick={handleClearConfiguration}
                  iconName="Trash2"
                  iconPosition="left"
                >
                  Limpiar Configuración
                </Button>

                <Button
                  variant="default"
                  onClick={() => navigate('/dashboard')}
                  iconName="Home"
                  iconPosition="left"
                >
                  Ir al Dashboard
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default APIConfiguration;